import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Copy, Check, Wallet, ArrowLeft, Loader } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import useEscapeKey from '../hooks/useEscapeKey';

const ReceiveFiresModal = ({ isOpen, onClose, walletAddress: walletAddressProp, onBack }) => {
  useEscapeKey(onClose, isOpen);
  const [copied, setCopied] = useState(false);
  const [fetchedAddress, setFetchedAddress] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();

  // Use the prop if available, otherwise use the fetched address
  const walletAddress = walletAddressProp || fetchedAddress;

  // If modal is open and we don't have an address, fetch from profile
  useEffect(() => {
    if (!isOpen) return;
    if (walletAddressProp) {
      setFetchedAddress(null);
      return;
    }
    if (fetchedAddress) return;
    if (!user?.id) return;

    let cancelled = false;
    const fetchWallet = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`/api/profile/${user.id}`);
        if (!cancelled && response.data?.wallet_address) {
          setFetchedAddress(response.data.wallet_address);
        }
      } catch (error) {
        console.error('Error fetching wallet address:', error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchWallet();
    return () => { cancelled = true; };
  }, [isOpen, walletAddressProp, fetchedAddress, user?.id]);

  // Reset fetched address when modal closes
  useEffect(() => {
    if (!isOpen) {
      setFetchedAddress(null);
      setCopied(false);
    }
  }, [isOpen]);

  const handleCopy = async () => {
    if (!walletAddress) {
      toast.error('No se encontró dirección de billetera');
      return;
    }
    try {
      await navigator.clipboard.writeText(walletAddress);
      setCopied(true);
      toast.success('Dirección copiada al portapapeles');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error('Error al copiar');
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
          onClick={handleBackdropClick}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="w-full max-w-md card-glass p-6"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => {
                    if (onBack) {
                      onBack();
                    } else {
                      onClose();
                      navigate('/profile?tab=fires');
                    }
                  }}
                  className="p-2 hover:bg-glass-hover rounded-lg transition-colors"
                >
                  <ArrowLeft size={20} />
                </button>
                <div className="w-10 h-10 rounded-full bg-fire-orange/20 flex items-center justify-center">
                  <Wallet size={20} className="text-fire-orange" />
                </div>
                <h2 className="text-xl font-bold">Recibir Fuegos</h2>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-glass-hover rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {/* Content */}
            <div className="space-y-4">
              <p className="text-text/70 text-sm">
                Comparte tu dirección de billetera para recibir fuegos de otros usuarios.
              </p>

              {/* Wallet Address Display */}
              <div className="p-4 rounded-xl bg-surface-1 border border-theme space-y-3">
                <label className="block text-xs font-bold text-text-secondary uppercase tracking-wider">
                  Tu Dirección de Billetera
                </label>
                <div className="bg-card border border-theme rounded-lg p-3 break-all font-mono text-sm text-primary font-bold min-h-[44px] flex items-center">
                  {loading ? (
                    <span className="flex items-center gap-2 text-text-secondary text-xs">
                      <Loader size={14} className="animate-spin text-primary" />
                      Cargando dirección…
                    </span>
                  ) : walletAddress ? (
                    walletAddress
                  ) : (
                    <span className="text-text-secondary text-xs">
                      No se encontró dirección de billetera
                    </span>
                  )}
                </div>
              </div>

              {/* Copy Button */}
              <button
                onClick={handleCopy}
                disabled={!walletAddress || loading}
                className="w-full btn-primary flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed py-3"
              >
                {copied ? (
                  <>
                    <Check size={20} />
                    ¡Copiado!
                  </>
                ) : (
                  <>
                    <Copy size={20} />
                    Copiar Dirección
                  </>
                )}
              </button>

              {/* Info */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
                <p className="text-xs text-blue-900 leading-relaxed">
                  💡 <strong>Nota:</strong> Otros usuarios necesitarán esta dirección para enviarte fuegos.
                  Las transferencias incluyen una comisión del 5%.
                </p>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ReceiveFiresModal;
